import React, { useRef, useState } from 'react';
import { Upload, X, ImagePlus, Loader2 } from 'lucide-react';
import { Button } from './button';
import { toast } from 'sonner';
import { uploadImages } from '../../lib/uploads';

/**
 * Reusable image-upload field.
 *
 * Props:
 *   - value: string[]              current list of CDN URLs (controlled)
 *   - onChange: (urls: string[]) => void
 *   - kind: "property"|"submission"|"service-request"
 *   - max: number                  max images allowed (default 10)
 *   - disabled?: boolean
 *
 * Behavior:
 *   - Click the dropzone or drag files to add
 *   - Each file is client-compressed then PUT to R2 in parallel
 *   - Shows a spinner per in-flight upload, an X to remove a finished one
 *   - Failed uploads toast + are not added to the list
 */
export function ImagesField({ value = [], onChange, kind, max = 10, disabled = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0); // count of in-flight uploads
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = async (fileList) => {
    if (disabled) return;
    const files = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) {
      toast.error('Please choose image files (JPG / PNG / WebP / GIF)');
      return;
    }
    const remaining = max - value.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${max} images allowed`);
      return;
    }
    const accepted = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.message(`Only ${remaining} more allowed — extra ${files.length - remaining} skipped`);
    }

    setUploading((n) => n + accepted.length);
    try {
      const results = await uploadImages(accepted, kind);
      const newUrls = [];
      let failures = 0;
      for (const r of results) {
        if (r.url) newUrls.push(r.url);
        else {
          failures++;
          toast.error(`${r.name}: ${r.error}`);
        }
      }
      if (newUrls.length) onChange([...value, ...newUrls]);
      if (failures === 0 && newUrls.length) {
        toast.success(`Uploaded ${newUrls.length} image${newUrls.length > 1 ? 's' : ''}`);
      }
    } finally {
      setUploading((n) => n - accepted.length);
    }
  };

  const handleInputChange = (e) => {
    handleFiles(e.target.files);
    // Reset so picking the same file twice still fires onChange
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleRemove = (idx) => {
    if (disabled) return;
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {/* Existing images */}
      {value.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {value.map((url, idx) => (
            <div
              key={url + idx}
              className="relative group aspect-square rounded-lg overflow-hidden border bg-gray-50"
            >
              <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(idx)}
                  aria-label="Remove image"
                  className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white rounded-full p-1 transition opacity-0 group-hover:opacity-100"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {/* In-flight upload placeholders */}
          {Array.from({ length: uploading }).map((_, i) => (
            <div
              key={`uploading-${i}`}
              className="aspect-square rounded-lg border border-dashed bg-gray-50 flex items-center justify-center"
            >
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ))}
        </div>
      )}

      {/* Dropzone */}
      {value.length < max && !disabled && (
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer transition ${
            dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={handleInputChange}
            className="hidden"
          />
          {uploading > 0 ? (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm text-gray-600">Uploading {uploading}…</p>
            </>
          ) : (
            <>
              {value.length === 0 ? (
                <Upload className="h-8 w-8 text-gray-400" />
              ) : (
                <ImagePlus className="h-8 w-8 text-gray-400" />
              )}
              <p className="text-sm font-medium text-gray-700">
                {value.length === 0 ? 'Click or drop images here' : 'Add more images'}
              </p>
              <p className="text-xs text-gray-500">
                JPG, PNG, WebP, GIF — up to {max} total ({value.length}/{max} used)
              </p>
            </>
          )}
        </label>
      )}

      {value.length >= max && (
        <p className="text-xs text-gray-500 text-center">
          Maximum {max} images reached — remove one to add another.
        </p>
      )}

      {value.length === 0 && (
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={disabled}>
          <ImagePlus className="h-4 w-4 mr-1" />
          Select images
        </Button>
      )}
    </div>
  );
}

export default ImagesField;
